import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, OnDestroy } from '@angular/core';
import { UtilService } from '../common/util.service';
import { CommonService } from '../common/common.service';
import { Router } from '@angular/router';

interface Message {
  sender: 'user' | 'bot';
  content: string;
  time: string;
}

interface QuickReply {
  label: string;
  action: string;
  value?: any;
}

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss'],
})
export class ContactUsComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('chatMessages', { static: false }) chatMessagesRef: ElementRef;
  
  // Chat state
  messages: Message[] = [];
  userMessage = '';
  conversationStarted = false;
  isTyping = false;
  showQuickReplies = false;
  quickReplies: QuickReply[] = [];
  
  // Form state
  showContactForm = false;
  isSubmitting = false;
  showMessage = false;
  messageType: 'success' | 'error' = 'error';
  displayMessage = '';
  
  // Form fields
  inquiryType = '';
  name = '';
  email = '';
  phone = '';
  subject = '';
  message = '';
  
  // Validation
  validEmail = '';
  characterOnlyErrorForName = '';
  isRobot = true;
  
  // UI state
  sidebarCollapsed = false;
  
  // WhatsApp configuration
  whatsappNumber = '233540124400';
  
  // Auto-scroll control
  private shouldScrollToBottom = true;

  // University Information
  universityInfo = {
    name: 'Techbridge University College',
    formerName: 'AsanSka University College of Design and Technology (AUCDT)',
    about: `Techbridge University College, formerly known as AsanSka University College of Design and Technology (AUCDT), 
            is a premier institution dedicated to excellence in technology, design, and communications education in Ghana. 
            We are committed to producing innovative graduates who drive technological advancement and sustainable development across Africa.`,
    vision: 'To be a leading technology-focused university in Africa, producing innovative graduates who drive technological advancement and sustainable development.',
    mission: 'To provide quality education in technology, design, and communications, preparing students for successful careers in the digital age.',
    programs: [
      'Bachelor of Technology in Fashion Design & Technology',
      'Bachelor of Technology in Digital Media & Technology',
      'Bachelor of Technology in Jewelry Design & Technology',
      'Bachelor of Technology in Product Design & Technology',
      'Diploma of Arts in Graphic Design & Technology',
      'Diploma in Digital Marketing',
      'Certificate Programs in Web Development, Mobile App Development, and UI/UX Design'
    ],
    whyChoose: [
      'Industry-Relevant Curriculum: Our programs are designed in collaboration with industry experts',
      'Modern Learning Facilities: State-of-the-art computer labs and design studios',
      'Experienced Faculty: Learn from professionals with real-world experience',
      'Career Support Services: Internship placement and job placement assistance',
      'Affordable Tuition: Quality education at competitive rates with flexible payment plans',
      'Flexible Learning Options: Evening and weekend classes available for working professionals'
    ],
    admissions: {
      requirements: [
        'WASSCE/SSSCE with passes in English and Mathematics',
        'Minimum aggregate of 24 for degree programs',
        'Mature students (25+) may apply through special entry',
        'Portfolio required for design programs'
      ],
      process: [
        'Complete online application form',
        'Submit required documents (transcripts, certificates)',
        'Pay application fee',
        'Attend interview/assessment (if required)',
        'Receive admission decision within 2 weeks'
      ],
      intakePeriods: ['September intake (Main)', 'January intake (Mid-year)']
    },
    contact: {
      phone: '+233 54 012 4400',
      email: 'info@techbridge.edu.gh',
      location: 'Oyibi, Accra, Ghana',
      hours: 'Monday - Friday: 8:00 AM - 5:00 PM'
    }
  };

  constructor(
    private router: Router,
    private utilService: UtilService,
    private commonService: CommonService
  ) {}

  ngOnInit() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.add('full-screen');
    body.classList.add('contact-us');
    const navbar = document.getElementsByTagName('nav')[0];
    navbar.classList.add('navbar-transparent');
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
    }
  }

  ngOnDestroy() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.remove('full-screen');
    body.classList.remove('contact-us');
    const navbar = document.getElementsByTagName('nav')[0];
    navbar.classList.remove('navbar-transparent');
  }

  selectTopic(topic: string) {
    this.conversationStarted = true;
    this.addUserMessage(`Tell me about ${topic}`);
    
    setTimeout(() => {
      this.isTyping = true;
      setTimeout(() => {
        this.isTyping = false;
        
        switch(topic) {
          case 'about':
            this.addBotMessage(this.getAboutInfo());
            this.showTopicQuickReplies();
            break;
          case 'programs':
            this.addBotMessage(this.getProgramsInfo());
            this.showTopicQuickReplies();
            break;
          case 'admissions':
            this.addBotMessage(this.getAdmissionsInfo());
            this.showTopicQuickReplies();
            break;
          case 'contact':
            this.addBotMessage(this.getContactInfo());
            this.showTopicQuickReplies();
            break;
        }
      }, 1500);
    }, 300);
  }

  sendMessage() {
    if (!this.userMessage.trim()) return;
    
    this.conversationStarted = true;
    const message = this.userMessage;
    this.addUserMessage(message);
    this.userMessage = '';
    
    setTimeout(() => {
      this.isTyping = true;
      setTimeout(() => {
        this.isTyping = false;
        this.processUserMessage(message);
      }, 1500);
    }, 300);
  }

  processUserMessage(message: string) {
    const lowerMessage = message.toLowerCase();
    
    // Check for common queries
    if (lowerMessage.includes('program') || lowerMessage.includes('course') || lowerMessage.includes('study')) {
      this.addBotMessage(this.getProgramsInfo());
    } else if (lowerMessage.includes('admission') || lowerMessage.includes('apply') || lowerMessage.includes('entry')) {
      this.addBotMessage(this.getAdmissionsInfo());
    } else if (lowerMessage.includes('about') || lowerMessage.includes('history') || lowerMessage.includes('techbridge')) {
      this.addBotMessage(this.getAboutInfo());
    } else if (lowerMessage.includes('contact') || lowerMessage.includes('phone') || lowerMessage.includes('email') || lowerMessage.includes('location')) {
      this.addBotMessage(this.getContactInfo());
    } else if (lowerMessage.includes('fee') || lowerMessage.includes('cost') || lowerMessage.includes('tuition') || lowerMessage.includes('payment')) {
      this.addBotMessage(this.getFeesInfo());
    } else if (lowerMessage.includes('facility') || lowerMessage.includes('campus') || lowerMessage.includes('lab')) {
      this.addBotMessage(this.getFacilitiesInfo());
    } else {
      // Default response
      this.addBotMessage(`
        <p>Thank you for your message! I'd be happy to help you with information about:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Our programs and courses</li>
          <li>Admissions requirements and process</li>
          <li>Tuition fees and payment options</li>
          <li>Campus facilities</li>
          <li>Contact information</li>
        </ul>
        <p>What would you like to know more about?</p>
      `);
    }
    
    this.showTopicQuickReplies();
  }

  handleQuickReply(reply: QuickReply) {
    this.showQuickReplies = false;
    
    if (reply.action === 'topic') {
      this.selectTopic(reply.value);
    } else if (reply.action === 'contact-form') {
      this.showContactForm = true;
    } else if (reply.action === 'whatsapp') {
      this.openWhatsApp();
    } else if (reply.action === 'call') {
      this.callUs();
    }
  }

  showTopicQuickReplies() {
    this.quickReplies = [
      { label: '📚 Programs', action: 'topic', value: 'programs' },
      { label: '📝 Admissions', action: 'topic', value: 'admissions' },
      { label: '💰 Fees', action: 'topic', value: 'fees' },
      { label: '📞 Contact Form', action: 'contact-form' }
    ];
    this.showQuickReplies = true;
  }

  addUserMessage(content: string) {
    this.messages.push({
      sender: 'user',
      content: content,
      time: this.getCurrentTime()
    });
    this.shouldScrollToBottom = true;
  }

  addBotMessage(content: string) {
    this.messages.push({
      sender: 'bot',
      content: content,
      time: this.getCurrentTime()
    });
    this.shouldScrollToBottom = true;
  }

  getCurrentTime(): string {
    const now = new Date();
    return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }

  scrollToBottom() {
    try {
      if (this.chatMessagesRef) {
        const element = this.chatMessagesRef.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  // University Information Methods
  getAboutInfo(): string {
    return `
      <p><strong>About ${this.universityInfo.name}</strong></p>
      <p>${this.universityInfo.about}</p>
      <p class="mt-2"><strong>Our Vision:</strong><br/>${this.universityInfo.vision}</p>
      <p class="mt-2"><strong>Our Mission:</strong><br/>${this.universityInfo.mission}</p>
      <p class="mt-2"><em>Formerly known as: ${this.universityInfo.formerName}</em></p>
    `;
  }

  getProgramsInfo(): string {
    const programsList = this.universityInfo.programs.map(p => `<li>${p}</li>`).join('');
    return `
      <p><strong>Our Academic Programs</strong></p>
      <p>We offer a wide range of programs designed to prepare you for the digital economy:</p>
      <ul style="margin: 8px 0; padding-left: 20px; line-height: 1.8;">
        ${programsList}
      </ul>
      <p class="mt-2">All programs combine theoretical knowledge with practical, hands-on experience.</p>
    `;
  }

  getAdmissionsInfo(): string {
    const requirementsList = this.universityInfo.admissions.requirements.map(r => `<li>${r}</li>`).join('');
    const processList = this.universityInfo.admissions.process.map(p => `<li>${p}</li>`).join('');
    
    return `
      <p><strong>Admissions Information</strong></p>
      
      <p><strong>Entry Requirements:</strong></p>
      <ul style="margin: 8px 0; padding-left: 20px;">
        ${requirementsList}
      </ul>
      
      <p class="mt-2"><strong>Application Process:</strong></p>
      <ol style="margin: 8px 0; padding-left: 20px;">
        ${processList}
      </ol>
      
      <p class="mt-2"><strong>Intake Periods:</strong> ${this.universityInfo.admissions.intakePeriods.join(' | ')}</p>
      
      <p class="mt-2">Ready to apply? Contact our admissions office for assistance!</p>
    `;
  }

  getContactInfo(): string {
    return `
      <p><strong>Get In Touch With Us</strong></p>
      
      <p><strong>📞 Phone:</strong><br/>
      <a href="tel:${this.universityInfo.contact.phone.replace(/\s/g, '')}">${this.universityInfo.contact.phone}</a></p>
      
      <p><strong>📧 Email:</strong><br/>
      <a href="mailto:${this.universityInfo.contact.email}">${this.universityInfo.contact.email}</a></p>
      
      <p><strong>📍 Location:</strong><br/>
      ${this.universityInfo.contact.location}</p>
      
      <p><strong>🕐 Office Hours:</strong><br/>
      ${this.universityInfo.contact.hours}</p>
      
      <p class="mt-2">You can also reach us via WhatsApp for quick responses!</p>
    `;
  }

  getFeesInfo(): string {
    return `
      <p><strong>Tuition Fees & Payment Information</strong></p>
      
      <p>We offer competitive tuition rates with flexible payment plans to make quality education accessible.</p>
      
      <p><strong>Fee Structure:</strong></p>
      <ul style="margin: 8px 0; padding-left: 20px;">
        <li>Degree Programs: Please contact admissions for current rates</li>
        <li>Diploma Programs: Please contact admissions for current rates</li>
        <li>Certificate Programs: Please contact admissions for current rates</li>
      </ul>
      
      <p class="mt-2"><strong>Payment Options:</strong></p>
      <ul style="margin: 8px 0; padding-left: 20px;">
        <li>Full payment discount available</li>
        <li>Semester installment plans</li>
        <li>Monthly payment options</li>
      </ul>
      
      <p class="mt-2"><strong>Scholarships:</strong> Merit-based and need-based scholarships available. Contact our financial aid office for details.</p>
      
      <p class="mt-2">For specific fee information, please contact our admissions office at <a href="mailto:${this.universityInfo.contact.email}">${this.universityInfo.contact.email}</a></p>
    `;
  }

  getFacilitiesInfo(): string {
    return `
      <p><strong>Our Campus Facilities</strong></p>
      
      <p>Techbridge University College is equipped with modern facilities to support your learning:</p>
      
      <ul style="margin: 8px 0; padding-left: 20px; line-height: 1.8;">
        <li><strong>Computer Labs:</strong> State-of-the-art labs with latest software and hardware</li>
        <li><strong>Design Studios:</strong> Professional-grade equipment for graphic design and multimedia</li>
        <li><strong>Library:</strong> Extensive collection of books, journals, and digital resources</li>
        <li><strong>WiFi Campus:</strong> High-speed internet access throughout the campus</li>
        <li><strong>Lecture Halls:</strong> Modern, air-conditioned classrooms with multimedia equipment</li>
        <li><strong>Student Hub:</strong> Collaborative spaces for group work and projects</li>
      </ul>
      
      <p class="mt-2">Visit our campus to experience our facilities firsthand!</p>
    `;
  }

  // Form Methods
  sendEmail() {
    this.showMessage = false;
    this.displayMessage = '';
    
    const validationError = this.validateFields();
    
    if (validationError) {
      this.messageType = 'error';
      this.displayMessage = validationError;
      this.showMessage = true;
      return;
    }
    
    if (this.isRobot) {
      this.messageType = 'error';
      this.displayMessage = 'Please complete the reCAPTCHA verification.';
      this.showMessage = true;
      return;
    }
    
    this.isSubmitting = true;
    
    const mailingData = JSON.stringify({
      senderEmailId: this.email,
      receiverEmailId: '',
      applicantId: '',
      message: this.message,
      fullName: this.name,
      subject: this.subject || `${this.inquiryType} - Techbridge Contact Form`,
      inquiryType: this.inquiryType,
      phone: this.phone,
    });
    
    this.commonService.sendContactUsEmail(mailingData).subscribe(
      (response) => {
        this.isSubmitting = false;
        this.messageType = 'success';
        this.displayMessage = '✓ Thank you! Your message has been sent successfully. We\'ll get back to you within 24 hours.';
        this.showMessage = true;
        
        // Add success message to chat
        this.addBotMessage(`
          <p><strong>✓ Message Sent Successfully!</strong></p>
          <p>Thank you, ${this.name}! We've received your ${this.inquiryType} inquiry and will respond to ${this.email} within 24 hours.</p>
        `);
        
        this.resetForm();
        this.closeContactForm();
        
        setTimeout(() => {
          this.showMessage = false;
        }, 5000);
      },
      (error) => {
        this.isSubmitting = false;
        this.messageType = 'error';
        this.displayMessage = 'Sorry, there was an error sending your message. Please try again or contact us directly.';
        this.showMessage = true;
      }
    );
  }

  resolved(captchaResponse: string) {
    if (captchaResponse) {
      this.isRobot = false;
    }
  }

  validateFields(): string | null {
    const errors = [];
    
    if (!this.inquiryType.trim()) errors.push('inquiry type');
    if (!this.name.trim()) errors.push('name');
    if (!this.email.trim()) errors.push('email');
    if (!this.message.trim()) errors.push('message');
    
    if (this.characterOnlyErrorForName) {
      return this.characterOnlyErrorForName;
    }
    
    if (this.validEmail) {
      return this.validEmail;
    }
    
    if (errors.length > 0) {
      return `Please fill in the required field(s): ${errors.join(', ')}`;
    }
    
    return null;
  }

  validateEmail(event: any) {
    if (event.target.value.length > 0 && !this.utilService.validateEmail(event.target.value)) {
      this.validEmail = 'Please enter a valid email address';
    } else {
      this.validEmail = '';
    }
  }

  characterOnly(event: any, elementName: string) {
    let characterOnlyError = '';
    if (event.target.value.length > 0 && !this.utilService.validateName(event.target.value)) {
      characterOnlyError = 'Name can only contain letters, spaces, and hyphens';
    }

    if (elementName === 'name') {
      this.characterOnlyErrorForName = characterOnlyError;
    }
  }

  resetForm() {
    this.inquiryType = '';
    this.name = '';
    this.email = '';
    this.phone = '';
    this.subject = '';
    this.message = '';
    this.validEmail = '';
    this.characterOnlyErrorForName = '';
    this.isRobot = true;
  }

  closeContactForm() {
    this.showContactForm = false;
    this.resetForm();
  }

  // UI Control Methods
  toggleMinimize() {
    // Implementation for minimize functionality
    console.log('Minimize chat');
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  openWhatsApp() {
    let defaultMessage = 'Hello Techbridge University College! I would like to inquire about ';
    
    if (this.inquiryType) {
      defaultMessage += this.inquiryType;
    } else {
      defaultMessage += 'your programs and admissions';
    }
    
    const encodedMessage = encodeURIComponent(defaultMessage);
    const whatsappUrl = `https://wa.me/${this.whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  }

  callUs() {
    window.location.href = `tel:${this.universityInfo.contact.phone.replace(/\s/g, '')}`;
  }
}